import React, { useCallback, useEffect, useState } from 'react';
import { config } from '../../../app-confi/app-config';
import { usePrevious } from '../../../common/react-helpers';
import { Building } from '../../../confi-state';
import { ServiceManager } from '../../../services/service-manager';
import { alignment } from '../models/alignment.enum';
import { buildingType } from '../models/building-type.enum';
import { ColumnService } from './column-service';
import { HybridService } from './hybrid.service';
import { columnType } from './models/column-type.enum';
import { crownType } from './models/crown-type.enum';
import { headType } from './models/head-type.enum';
import { alignsWithBuilding, defaultHeadLeft, defaultHeadRight, headTypesModel } from './models/head-types.model';
import { plinthType } from './models/plinth-type.enum';
import { getDefaultPropsHybrid, getDefaultPropsSingleLoadedCorridor, PropsHybrid } from './models/props-config';
import { FormControl, FormLabel, FormControlLabel, RadioGroup, Radio, FormGroup, Switch, Select, MenuItem, InputLabel, SelectChangeEvent } from '@mui/material';
import { Point3 } from '../../../common/models/point.type';
import { InitialProps } from '../Common/BuildingInitialPropertiesDialog/init-props';
import { columnDefs, columnSubtype } from './models/column-definition';
import NumberArrowSelector from '../../Common/Inputs/NumberArrowSelector/NumberArrowSelector';
import buildingConfStyles from '../BuildingConf.module.css';
import { segmentType } from './models/segment-type';
import { SectionJSON, UnitJ } from '../../../models/section-json';
import { ColDefOption } from '../../../app-confi/services/building-helper.service';
import { inclusionEnum } from '../models/ColumnDefinition/column-definition';


const buildingChange = (buildingProps: PropsHybrid, currentBuilding: Building, onChangeBuilding) => {
    onChangeBuilding(currentBuilding.projectId, currentBuilding.siteId, { ...currentBuilding, props: { ...buildingProps } }, false);
};

const Hybrid = ({
    currentBuilding,
    buildingScaleDimensions,
    previousBuildingDimensions,
    initProps,
    sectionJson,
    onChangeBuilding }: {
        currentBuilding: Building,
        buildingScaleDimensions: Point3,
        previousBuildingDimensions: Point3,
        initProps: InitialProps,
        sectionJson: SectionJSON,
        onChangeBuilding: (projectId: number | string, siteId: number | string, building: Building, saveToDb: boolean) => void
    }) => {

    const [buildingLengthUpdatedByColumns, setBuildingLengthUpdatedByColumns] = useState<boolean>(
        false
    );

    const [buildingProps, setBuildingProps] = useState<PropsHybrid>(
        undefined
    );

    const [moosPlazaBays, setMoosPlazaBays] = useState<number>(undefined);
    const previousMoosPlazaBays = usePrevious<number>(moosPlazaBays);
    const [moosPlazaBaysOptions, setMoosPlazaBaysOptions] = useState<{ bays: number, squareMeters: number }[]>([]);
    const [moosPlazaTopOptions, setMoosPlazaTopOptions] = useState<{
        forMoosPlazaBays: number,
        options: ColDefOption<columnType | headType>[]
    }[]>(
        []
    );
    const [headAlignmentOptions, setHeadAlignmentOptions] = useState<{ alignment: alignsWithBuilding, options: ColDefOption<columnType | headType>[] }[]>([
        { alignment: alignsWithBuilding.parallel, options: [] },
        { alignment: alignsWithBuilding.perpendicular, options: [] }
    ]);

    const previousBuildingScaleDimensions = usePrevious<Point3>(buildingScaleDimensions);

    const maxLength = 1000;
    const maxHeight = 25;
    const maxWidth = 100;
    const minLength = 20;
    const minWidth = 20;
    const minHeight = 9;

    const recomputeColumns = useCallback((computeAmounts: boolean, bp: PropsHybrid) => {
        let b = { ...bp };
        if (computeAmounts) {
            const amounts = HybridService.getAmountsByAvailableBays(b);
            b = { ...b, ...amounts };
        }

        const columnService = new ColumnService();
        b.parts = columnService.createPossibleColumnDefinitions({
            amountOfGMColumns: b.amount_G_M_Columns,
            amountOfGS1S2Columns: b.amount_G_S1S2_Columns,
            amountOfGL1L2Columns: b.amount_G_L1L2_Columns,
            amountOfGXLColumns: b.amount_G_XL_Columns,
            selectedPlinthType: b.plinthType,
            selectedMiddleType: b.middleType,
            selectedCrownType: b.crownType,
            plinthFloors: b.plinthFloors,
            middleFloors: ServiceManager.buildingHelperService.getMiddleFloors(b.plinthType, b.buildingHeight, b.customPlinthHeight, b.crownFloors, b.plinthFloors, buildingType.hybrid),
            crownFloors: b.crownFloors,
            selectedMoosPlazaType: b.moosPlazaType,
            selectedMoosPlazaAlignment: b.moosPlazaAlignment,
            headLeftType: b.headLeftType,
            headRightType: b.headRightType,
            nrOfStaircaseCores: HybridService.getStaircaseCores(b),
            nrOfElevatorCores: HybridService.getElevatorCoresThatShouldBeAdded(b),
            customPlinthHeight: b.customPlinthHeight,
            customPlinthWidth: b.customPlinthWidth,
        });

        b.parts = ServiceManager.babylonService.hybridBabylonService.mapPartsTo3D(
            b.parts,
            b.moosPlazaAlignment,
        );
        b.totalAmountOfAllUnits = columnService.getTotalAmountOfAllUnits(b.parts);
        b.info = ServiceManager.buildingHelperService.computePartInfo(b.parts as any);

        setBuildingProps({ ...b });
        buildingChange({ ...b }, currentBuilding, onChangeBuilding);
    }, [currentBuilding, onChangeBuilding]);

    useEffect(() => {
        if (currentBuilding.props) {
            setBuildingProps({ ...currentBuilding.props as PropsHybrid });
            // migrate
            const columns = (currentBuilding.props as any).columns;
            if (columns !== undefined) {
                delete (currentBuilding.props as any).columns;
                recomputeColumns(true, currentBuilding.props as PropsHybrid);
            }
        }
    }, [currentBuilding.props, recomputeColumns])

    useEffect(() => {
        if (currentBuilding && !currentBuilding.props) {
            let props: PropsHybrid;
            if (currentBuilding.type === buildingType.hybrid) {
                props = getDefaultPropsHybrid(initProps);
                if (previousBuildingDimensions && (previousBuildingDimensions[0] !== 0 && previousBuildingDimensions[1] !== 0 && previousBuildingDimensions[2] !== 0)) {
                    props.buildingLength = ServiceManager.utilityService.keepInBounds(previousBuildingDimensions[0], minLength, maxLength);
                    props.buildingHeight = ServiceManager.utilityService.keepInBounds(previousBuildingDimensions[1], minHeight, maxHeight);
                }
            } else if (currentBuilding.type === buildingType.singleLoadedCorridor) {
                props = getDefaultPropsSingleLoadedCorridor(initProps);
            }
            recomputeColumns(true, { ...props });
        } else if (buildingProps && moosPlazaBays === undefined) {
            setMoosPlazaBays(HybridService.getColumnBays(buildingProps.moosPlazaType));
            const options = getMoosPlazaBayData();
            setMoosPlazaBaysOptions(options);
            const topAvailableOptions = getMoosPlazaTopOptionsForAvailableBays(options.map(o => o.bays));
            setMoosPlazaTopOptions(topAvailableOptions);

            const parallellOptions = getHeadOptionsForAlignment(alignsWithBuilding.parallel);
            const perpendicularOptions = getHeadOptionsForAlignment(alignsWithBuilding.perpendicular);
            setHeadAlignmentOptions([{
                alignment: alignsWithBuilding.parallel, options: parallellOptions
            },
            {
                alignment: alignsWithBuilding.perpendicular, options: perpendicularOptions
            }])

        }
    }, [buildingProps, currentBuilding, previousBuildingDimensions, onChangeBuilding, recomputeColumns])

    useEffect(() => {
        if (buildingScaleDimensions && buildingProps && (
            buildingScaleDimensions[0] !== previousBuildingScaleDimensions[0] ||
            buildingScaleDimensions[1] !== previousBuildingScaleDimensions[1] ||
            buildingScaleDimensions[2] !== previousBuildingScaleDimensions[2]
        ) &&
            (
                buildingScaleDimensions[0] !== 0 &&
                buildingScaleDimensions[1] !== 0 &&
                buildingScaleDimensions[2] !== 0
            )
        ) {
            const b = { ...buildingProps };

            const tolerance = 0.1;
            const updatedLength = Math.abs(buildingProps.buildingLength - buildingScaleDimensions[0]) > tolerance;
            const updatedHeight = Math.abs(buildingProps.buildingHeight - buildingScaleDimensions[1]) > tolerance;
            const updatedWidth = Math.abs(buildingProps.buildingWidth - buildingScaleDimensions[2]) > tolerance;

            if (updatedLength) {
                b.buildingLength = ServiceManager.utilityService.keepInBounds(buildingScaleDimensions[0], minLength, maxLength);
            }
            if (updatedHeight) {
                b.buildingHeight = ServiceManager.utilityService.keepInBounds(buildingScaleDimensions[1], minHeight, maxHeight);
            }
            if (updatedWidth) {
                b.buildingWidth = ServiceManager.utilityService.keepInBounds(buildingScaleDimensions[2], minWidth, maxWidth)
            }

            if ((updatedLength && !updatedHeight) || (updatedLength && updatedHeight)) {
                recomputeColumns(true, b);
            } else {
                recomputeColumns(false, b);
            }
        }
    }, [buildingScaleDimensions, previousBuildingScaleDimensions, buildingProps, recomputeColumns]);

    const getMoosPlazaBayData = (): { bays: number, squareMeters: number }[] => {
        const s = columnDefs.map(s => {
            let bays = 0;
            let squareMeters = 0;
            if (s.columnSubtypes.includes(columnSubtype.moosplaza)) {
                bays = s.bays;
                const plinthDef = s.segmentDefinitions.find(sd => sd.type === segmentType.plinth);
                const sectionType = plinthDef.floorVariants[0].variants[0].sections[0];
                const sectionDescription = sectionJson.sections.find(s => s.sectionType === sectionType);
                if (sectionDescription) {
                    const moosPlazaAmenity = sectionJson.amenities.find(a => a.amenityType === sectionDescription.amenities[0].amenityType);
                    if (moosPlazaAmenity) {
                        squareMeters = moosPlazaAmenity.GFA;
                    }
                }
            }
            return { bays, squareMeters }
        });
        const baySqm = s.filter(s => s.bays !== 0);
        const uniqueBays = [];
        baySqm.forEach(bs => {
            if (!uniqueBays.find(s => s?.bays === bs.bays)) {
                uniqueBays.push(bs);
            }
        })
        return uniqueBays;
    }

    useEffect(() => {
        if (moosPlazaBays) {
            if (previousMoosPlazaBays !== undefined && previousMoosPlazaBays !== moosPlazaBays) {
                const option = moosPlazaTopOptions.find(s => s.forMoosPlazaBays === moosPlazaBays);
                updateMoosPlazaType(option.options[0].type);
            }
        }
        if (moosPlazaBays === undefined && buildingProps && buildingProps.moosPlazaType !== columnType.none) {
            setMoosPlazaBays(HybridService.getColumnBays(buildingProps.moosPlazaType));
        }
    }, [moosPlazaBays])


    const handleChange = (e: React.FocusEvent<HTMLInputElement>, type: 'width' | 'length' | 'height' | 'customPlinthWidth' | 'customPlinthHeight') => {
        const value = +(e.target as any).value as number;
        let bp = { ...buildingProps };
        switch (type) {
            case 'width':
                bp.buildingWidth = value < maxWidth ? value : maxWidth;
                (e.target as HTMLInputElement).value = bp.buildingWidth.toString();
                setBuildingProps({ ...bp });
                buildingChange({ ...bp }, currentBuilding, onChangeBuilding);
                break;
            case 'length':
                bp.buildingLength = value < maxLength ? value : maxLength;
                (e.target as HTMLInputElement).value = bp.buildingLength.toString();
                // this is done to avoid circular computations. When amount of columns get updated.
                if (!buildingLengthUpdatedByColumns) {
                    recomputeColumns(true, bp);
                } else {
                    setBuildingLengthUpdatedByColumns(false);
                }
                break;
            case 'height':
                bp.buildingHeight = value < maxHeight ? value : maxHeight;

                if (bp.buildingHeight > config.highRiseLimit) {
                    const currentHeadLeft = headTypesModel.find(head => head.type === bp.headLeftType);
                    const currentHeadRight = headTypesModel.find(head => head.type === bp.headRightType);
                    if (!currentHeadLeft.highrise) {
                        bp.headLeftType = defaultHeadLeft;
                    }
                    if (!currentHeadRight.highrise) {
                        bp.headRightType = defaultHeadRight;
                    }
                }

                if (HybridService.getExtraElevators(bp) > 0) {
                    if (bp.moosPlazaType !== columnType.V_P4H && bp.moosPlazaType !== columnType.V_P5H_XL && bp.moosPlazaType !== columnType.V_P5H_S1S2) {
                        bp.moosPlazaType = columnType.V_P4H;
                    }
                    const headTypeModelLeftIncludes = columnDefs.find(head => head.type === bp.headLeftType).inclusions;
                    const headTypeModelRightIncludes = columnDefs.find(head => head.type === bp.headRightType).inclusions;

                    if (!headTypeModelLeftIncludes.some(s => s === inclusionEnum.elevator)) {
                        bp.headLeftType = headType._O_TLH2;
                    }
                    if (!headTypeModelRightIncludes.some(s => s === inclusionEnum.elevator)) {
                        bp.headRightType = headType._O_TLH2;
                    }
                }
                recomputeColumns(false, bp);

                break;
            case 'customPlinthHeight':
                bp.customPlinthHeight = value;
                recomputeColumns(false, bp);
                break;
            case 'customPlinthWidth':
                bp.customPlinthWidth = value;
                recomputeColumns(false, bp);
                break;
            default:
                alert('unknown value type provided');
                break;
        }
    }

    const updateCustomPlinth = () => {
        let bp = { ...buildingProps };
        if (bp.plinthType === plinthType.Custom) {
            bp.plinthType = plinthType.G;
            bp.plinthFloors = 2;
            bp.customPlinthWidth = 0;
            bp.customPlinthHeight = 0;
        } else {
            bp.plinthType = plinthType.Custom;
            bp.moosPlazaType = columnType.none;
            bp.plinthFloors = 1;
            if (!bp.customPlinthWidth) {
                bp.customPlinthWidth = 10;
            }
            if (!bp.customPlinthHeight) {
                bp.customPlinthHeight = config.hybridFloorHeight * 2;
            }
        }
        recomputeColumns(false, bp);
    }

    const updateNoneMoosPlaza = () => {
        let bp = { ...buildingProps };
        if (bp.moosPlazaType === columnType.none) {
            bp.moosPlazaType = columnType.V_P3;
            setMoosPlazaBays(3);
        } else {
            bp.moosPlazaType = columnType.none;
            bp.moosPlazaAlignment = alignment.center;
        }
        recomputeColumns(false, bp);
    }

    const updateMoosPlazaType = (type: columnType | headType) => {
        let bp = { ...buildingProps };
        bp.moosPlazaType = type as columnType;
        recomputeColumns(false, bp);
    }

    const updateNoneHeadLeft = () => {
        let bp = { ...buildingProps };
        if (bp.headLeftType === headType.none) {
            bp.headLeftType = headTypesModel.find(s => s.alignsWithBuilding === alignsWithBuilding.perpendicular).type;
        } else {
            bp.headLeftType = headType.none;
        }
        recomputeColumns(false, bp);
    }

    const updateNoneHeadRight = () => {
        let bp = { ...buildingProps };
        if (bp.headRightType === headType.none) {
            bp.headRightType = headTypesModel.find(s => s.alignsWithBuilding === alignsWithBuilding.perpendicular).type;
        } else {
            bp.headRightType = headType.none;
        }
        recomputeColumns(false, bp);
    }

    const updateHeadLeftAlignemntOptions = () => {
        let bp = { ...buildingProps };
        const currentAlignment = HybridService.getHeadTypeAlignmentToBuilding(buildingProps.headLeftType);
        if (currentAlignment === alignsWithBuilding.parallel) {
            bp.headLeftType = headTypesModel.find(s => s.alignsWithBuilding === alignsWithBuilding.perpendicular).type;
        } else if (currentAlignment === alignsWithBuilding.perpendicular) {
            bp.headLeftType = headTypesModel.find(s => s.alignsWithBuilding === alignsWithBuilding.parallel).type;
        }
        recomputeColumns(false, bp);
    }

    const updateHeadRightAlignemntOptions = () => {
        let bp = { ...buildingProps };
        const currentAlignment = HybridService.getHeadTypeAlignmentToBuilding(buildingProps.headRightType);
        if (currentAlignment === alignsWithBuilding.parallel) {
            bp.headRightType = headTypesModel.find(s => s.alignsWithBuilding === alignsWithBuilding.perpendicular).type;
        } else if (currentAlignment === alignsWithBuilding.perpendicular) {
            bp.headRightType = headTypesModel.find(s => s.alignsWithBuilding === alignsWithBuilding.parallel).type;
        }
        recomputeColumns(false, bp);
    }

    const handleSelect = (e: React.FormEvent<HTMLInputElement | HTMLSelectElement> | SelectChangeEvent<any>, type: 'plinthType' | 'middleType' | 'crownType' | 'crownFloors' | 'plinthFloors' | 'moosPlazaType' | 'moosPlazaAlignment' | 'headLeftType' | 'headRightType') => {
        let bp = { ...buildingProps };
        const value = (e.target as any).value as any;
        switch (type) {
            case 'plinthType':
                bp.plinthType = value;
                if (bp.plinthType === plinthType.G) {
                    bp.plinthFloors = 1;
                } else if (bp.plinthType === plinthType.H) {
                    bp.plinthFloors = 2;
                }
                bp.customPlinthWidth = 0;
                bp.customPlinthHeight = 0;
                recomputeColumns(false, bp);
                break;
            case 'middleType':
                bp.middleType = value;
                recomputeColumns(false, bp);
                break;
            case 'crownType':
                bp.crownType = value;
                if (bp.crownType === crownType.G) {
                    bp.crownFloors = 1;
                } else if (bp.crownType === crownType.H) {
                    bp.crownFloors = 2;
                }
                recomputeColumns(false, bp);
                break;
            case 'crownFloors':
                bp.crownFloors = +value;
                recomputeColumns(false, bp);
                break;
            case 'plinthFloors':
                bp.plinthFloors = +value;
                recomputeColumns(false, bp);
                break;
            case 'moosPlazaType':
                bp.moosPlazaType = value;
                if (bp.moosPlazaType === columnType.none) {
                    bp.moosPlazaAlignment = alignment.center;
                }
                recomputeColumns(false, bp);
                break;
            case 'moosPlazaAlignment':


                if (bp.moosPlazaAlignment === alignment.left && value !== alignment.left) {
                    bp.headLeftType = defaultHeadLeft;
                } else if (bp.moosPlazaAlignment === alignment.right && value !== alignment.right) {
                    bp.headRightType = defaultHeadRight;
                }

                if (value === alignment.left) {
                    bp.headLeftType = headType.none;
                } else if (value === alignment.right) {
                    bp.headRightType = headType.none;
                }

                bp.moosPlazaAlignment = value;
                recomputeColumns(false, bp);
                break;
            case 'headLeftType':
                bp.headLeftType = value;
                recomputeColumns(false, bp);
                break;
            case 'headRightType':
                bp.headRightType = value;
                recomputeColumns(false, bp);
                break;
            default:
                alert('unknown value type provided');
                break;
        }
    }


    const getRealLength = () => {
        let result = 0;
        result += HybridService.getBodySizeForComputationBasedOnColumnsAmount(buildingProps) * config.hybridBaySize;
        if (buildingProps.headLeftType !== headType.none) {
            result += columnDefs.find(s => s.type === buildingProps.headLeftType).bays * config.hybridBaySize;
        }
        if (buildingProps.headRightType !== headType.none) {
            result += columnDefs.find(s => s.type === buildingProps.headRightType).bays * config.hybridBaySize;
        }
        return result;
    }

    const handleColumnTypeChange = (increment: boolean, type: string) => {
        let bp = { ...buildingProps };
        switch (type) {
            case columnType.G_M:
                bp.amount_G_M_Columns = increment ? bp.amount_G_M_Columns + 1 : bp.amount_G_M_Columns - 1;
                break;
            case columnType.G_XL:
                bp.amount_G_XL_Columns = increment ? bp.amount_G_XL_Columns + 1 : bp.amount_G_XL_Columns - 1;
                break;
            case columnType.G_L1L2:
                bp.amount_G_L1L2_Columns = increment ? bp.amount_G_L1L2_Columns + 1 : bp.amount_G_L1L2_Columns - 1;
                break;
            case columnType.G_S1S2:
                bp.amount_G_S1S2_Columns = increment ? bp.amount_G_S1S2_Columns + 1 : bp.amount_G_S1S2_Columns - 1;
                break;
            default:
                alert('unknown value type provided');
                break;
        }
        bp.buildingLength = getRealLength();
        recomputeColumns(false, bp);
    }

    const getMoosPlazaTopOptionsForAvailableBays = (availableBays: number[]): {
        forMoosPlazaBays: number,
        options: ColDefOption<columnType | headType>[]
    }[] => {

        let res = availableBays.map(bay => {
            const moosPlazaColumnsOfBaySize = columnDefs.filter(c => c.columnSubtypes.includes(columnSubtype.moosplaza) && c.bays === bay)
            const s = moosPlazaColumnsOfBaySize.map(colDef => {
                return ServiceManager.buildingHelperService.getColumnOptions(colDef, segmentType.middle, sectionJson);
            });
            return { forMoosPlazaBays: bay, options: s }
        });
        return res;
    };

    const getHeadOptionsForAlignment = (alignment: alignsWithBuilding) => {
        const headDefinitionsForAlignment = columnDefs.filter(c =>
            c.columnSubtypes.includes(columnSubtype.head) &&
            headTypesModel.find(s => s.type === c.type).alignsWithBuilding === alignment);
        const options = headDefinitionsForAlignment.map(colDef => {
            return ServiceManager.buildingHelperService.getColumnOptions(colDef, segmentType.middle, sectionJson);
        });
        return options;
    }

    const getSelectFormTopOptions = (bays: number) => {
        const moptions = moosPlazaTopOptions.find(o => o.forMoosPlazaBays === bays);
        return moptions && moptions.options.some(o => o.type === buildingProps.moosPlazaType) && moptions.forMoosPlazaBays === moosPlazaBays &&
            <div className={buildingConfStyles.formControl}>
                <FormControl size="medium" fullWidth >
                    <InputLabel id={"moosplaza-top-selection-label"}>Woningen boven entree</InputLabel>
                    <Select
                        labelId={"moosplaza-top-selection-label"}
                        id={"moosplaza-top-selection-id"}
                        data-testid="moosplaza-top-selection-id"
                        value={buildingProps.moosPlazaType}
                        onChange={(event) => { updateMoosPlazaType(event.target.value as columnType) }}
                        label="Woningen boven entree"
                    >
                        {
                            moptions.options.map((bs, index) => {
                                return (
                                    <MenuItem data-testid={'moosplaza-top-selection-' + index} key={'moosplaza-top-selection-' + index} value={bs.type}>
                                        <div className={buildingConfStyles.selectDetails}>
                                            <div>
                                                <span className={buildingConfStyles.boldLabel}>{bs.name}</span>
                                                <span className={buildingConfStyles.smallExplanation}>
                                                    &nbsp;en {bs.subname}
                                                </span>
                                            </div>
                                            <div>
                                                {bs.unitDetails.map((s, index) => {
                                                    return <div key={'detail-' + index} className={buildingConfStyles.smallExplanation}>
                                                        {s.unit} unit - {s.details}
                                                    </div>
                                                })}
                                            </div>
                                        </div>
                                    </MenuItem>
                                )
                            })
                        }
                    </Select>
                </FormControl>
            </div>
    }

    const getSelectFormHeadOptions = (orientation: 'left' | 'right') => {
        const alignment = HybridService.getHeadTypeAlignmentToBuilding(orientation === 'left' ? buildingProps.headLeftType : buildingProps.headRightType);
        const hoptions = headAlignmentOptions.find(ha => ha.alignment === alignment).options;
        return hoptions && hoptions.length &&
            <div className={buildingConfStyles.formControl}>
                <FormControl id={`head-${orientation}-selection-control`} size="medium" fullWidth >
                    <InputLabel id={`head-${orientation}-selection-label`}>{orientation === 'left' ? 'Kop 1' : 'Kop 2'}</InputLabel>
                    <Select
                        labelId={`head-${orientation}-selection-label`}
                        id={`head-${orientation}-selection-id`}
                        data-testid={`head-${orientation}-selection-id`}
                        value={orientation === 'left' ? buildingProps.headLeftType : buildingProps.headRightType}
                        onChange={(event) => { orientation === 'left' ? handleSelect(event, 'headLeftType') : handleSelect(event, 'headRightType') }}
                        label={orientation === 'left' ? 'Kop 1' : 'Kop 2'}
                    >
                        {
                            hoptions.map((bs, index) => {
                                return (
                                    <MenuItem data-testid={`head-${orientation}-selection-${index}`} key={`head-${orientation}-selection-${index}`} value={bs.type}>
                                        <div className={buildingConfStyles.selectDetails}>
                                            <div>
                                                <span className={buildingConfStyles.boldLabel}>{bs.name}</span>
                                                {bs.subname &&
                                                    <span className={buildingConfStyles.smallExplanation}>
                                                        &nbsp;en {bs.subname}
                                                    </span>
                                                }
                                            </div>
                                            <div>
                                                {bs.unitDetails.map((s, index) => {
                                                    return <div key={'detail-' + index} className={buildingConfStyles.smallExplanation}>
                                                        {s.unit} unit - {s.details}
                                                    </div>
                                                })}
                                            </div>
                                        </div>
                                    </MenuItem>
                                )
                            })
                        }
                    </Select>
                </FormControl>
            </div>
    }

    const getValueOfMoosPlazaBaysOptions = () => {
        return HybridService.getColumnBays(buildingProps.moosPlazaType)
    }

    return (
        <div data-testid="Hybrid">
            {buildingProps &&
                <>
                    <div className="group">
                        <h2 className="greyed-out-text">Kolommen aanpassen</h2>
                        <div>
                            <div className={'dimensions ' + buildingConfStyles.columnNr}>
                                <NumberArrowSelector
                                    label="S"
                                    min={0}
                                    max={100}
                                    value={buildingProps.amount_G_S1S2_Columns}
                                    increment={() => handleColumnTypeChange(true, columnType.G_S1S2)}
                                    decrement={() => handleColumnTypeChange(false, columnType.G_S1S2)}
                                />
                                <NumberArrowSelector
                                    label="M"
                                    min={0}
                                    max={100}
                                    value={buildingProps.amount_G_M_Columns}
                                    increment={() => handleColumnTypeChange(true, columnType.G_M)}
                                    decrement={() => handleColumnTypeChange(false, columnType.G_M)}
                                />
                                <NumberArrowSelector
                                    label="L"
                                    min={0}
                                    max={100}
                                    value={buildingProps.amount_G_L1L2_Columns}
                                    increment={() => handleColumnTypeChange(true, columnType.G_L1L2)}
                                    decrement={() => handleColumnTypeChange(false, columnType.G_L1L2)}
                                />
                                <NumberArrowSelector
                                    label="XL"
                                    min={0}
                                    max={100}
                                    value={buildingProps.amount_G_XL_Columns}
                                    increment={() => handleColumnTypeChange(true, columnType.G_XL)}
                                    decrement={() => handleColumnTypeChange(false, columnType.G_XL)}
                                />
                            </div>
                            <div className="extras">
                            </div>
                        </div>
                    </div>
                    <hr></hr>
                    <div className="group">
                        <h2 className="greyed-out-text">Plint indelen</h2>
                        <FormGroup>
                            <FormControlLabel control={<Switch
                                checked={buildingProps.plinthType === plinthType.Custom}
                                onChange={updateCustomPlinth}
                            />} label="Maatwerk plint" />
                            <FormControlLabel disabled={buildingProps.plinthType === plinthType.Custom} control={<Switch
                                checked={buildingProps.moosPlazaType !== columnType.none}
                                onChange={updateNoneMoosPlaza}
                            />} label="Entree" />
                        </FormGroup>
                        {
                            buildingProps.moosPlazaType !== columnType.none && moosPlazaBays !== undefined && moosPlazaTopOptions.length > 0 &&
                            <>
                                {
                                    moosPlazaBaysOptions.length > 0 &&
                                    <div className={buildingConfStyles.formControl}>
                                        <FormControl size="medium" fullWidth>
                                            <InputLabel id={"moosplaza-bays-selection-label"}>Entree breedte</InputLabel>
                                            <Select
                                                labelId={"moosplaza-bays-selection-label"}
                                                id={"moosplaza-bays-selection-id"}
                                                data-testid="moosplaza-bays-selection-id"
                                                value={getValueOfMoosPlazaBaysOptions()}
                                                onChange={(event) => {
                                                    setMoosPlazaBays(+event.target.value)
                                                }}
                                                label="Entree breedte"
                                            >
                                                {
                                                    moosPlazaBaysOptions.map((bs, index) => {
                                                        return (
                                                            <MenuItem data-testid={'moosplaza-bays-selection-' + index} key={'moosplaza-bays-selection-' + index} value={bs.bays}>
                                                                <div>
                                                                    <span className={buildingConfStyles.boldLabel}>{bs.bays} beuken</span> - {bs.squareMeters} ㎡
                                                                </div>
                                                            </MenuItem>
                                                        )
                                                    })
                                                }
                                            </Select>
                                        </FormControl>
                                    </div>
                                }
                                {
                                    getSelectFormTopOptions(moosPlazaBays)
                                }
                                <div className={buildingConfStyles.formControl}>
                                    <FormControl size="medium" fullWidth>
                                        <InputLabel id={"moosplaza-alignment-selection-label"}>Positie</InputLabel>
                                        <Select
                                            labelId={"moosplaza-alignment-selection-label"}
                                            id={"moosplaza-alignment-selection-id"}
                                            data-testid="moosplaza-alignment-selection-id"
                                            value={buildingProps.moosPlazaAlignment}
                                            onChange={(event) => {
                                                handleSelect(event, 'moosPlazaAlignment')
                                            }}
                                            label="Positie"
                                        >
                                            <MenuItem data-testid={'moosplaza-alignment-selection-' + 0} value={alignment.left}>
                                                Links
                                            </MenuItem>
                                            <MenuItem data-testid={'moosplaza-alignment-selection-' + 0} value={alignment.center}>
                                                Midden
                                            </MenuItem>
                                            <MenuItem data-testid={'moosplaza-alignment-selection-' + 0} value={alignment.right}>
                                                Rechts
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                </div>
                            </>
                        }
                        <div className={buildingConfStyles.formControl}>
                            {currentBuilding.type === buildingType.hybrid && buildingProps.plinthType !== plinthType.Custom &&
                                <FormControl component="fieldset">
                                    <FormLabel component="legend">Plint type</FormLabel>
                                    <RadioGroup
                                        row
                                        aria-label="plint type"
                                        name="controlled-radio-buttons-group-plinth"
                                        value={buildingProps.plinthType}
                                        onChange={(event) => handleSelect(event, 'plinthType')}
                                    >
                                        <FormControlLabel value={plinthType.H} control={<Radio size="small" />} label="Maisonettes" />
                                        <FormControlLabel value={plinthType.G} control={<Radio size="small" />} label="Appartementen" />
                                    </RadioGroup>
                                </FormControl>
                            }
                            {
                                buildingProps.plinthType === plinthType.H || buildingProps.plinthType === plinthType.Custom ?
                                    <dl>
                                        <dt>
                                            Plint verdiepingen</dt>
                                        <dd>
                                            {buildingProps.plinthFloors}
                                        </dd>
                                    </dl>
                                    :
                                    <div className={buildingConfStyles.formControl}>
                                        <FormControl size="medium" fullWidth>
                                            <InputLabel id={"plint-floors-selection-label"}>Plint verdiepingen</InputLabel>
                                            <Select
                                                labelId={"plint-floors-selection-label"}
                                                id={"plint-floors-selection-id"}
                                                data-testid="plint-floors-selection-id"
                                                value={buildingProps.plinthFloors}
                                                onChange={(e) => {
                                                    handleSelect(e as any, 'plinthFloors')
                                                }}
                                                // onInput={(event) => handleSelect(event as any, 'plinthFloors')}
                                                label="Plint verdiepingen"
                                            >
                                                <MenuItem data-testid={'alignment-selection-' + 0} value={1}>
                                                    1
                                                </MenuItem>
                                                <MenuItem data-testid={'alignment-selection-' + 0} value={2}>
                                                    2
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                    </div>
                            }
                        </div>
                        {buildingProps.plinthType === plinthType.Custom &&
                            <div >
                                <label>
                                    Custom Plint Breedte (m)
                                    <input type="number" value={buildingProps.customPlinthWidth}
                                        onChange={(e) => { setBuildingProps({ ...buildingProps, customPlinthWidth: +e.target.value }) }}
                                        onBlur={(e) => handleChange(e, 'customPlinthWidth')} placeholder="breedte"></input>
                                </label>
                                <label>
                                    Custom Plint Hoogte (m)
                                    <input type="number" value={buildingProps.customPlinthHeight}
                                        onChange={(e) => { setBuildingProps({ ...buildingProps, customPlinthHeight: +e.target.value }) }}
                                        onBlur={(e) => handleChange(e, 'customPlinthHeight')} placeholder="hoogte"></input>
                                </label>
                            </div>
                        }
                    </div>
                    {currentBuilding.type === buildingType.hybrid &&
                        (<div className="group">
                            <div >
                                <FormControl component="fieldset">
                                    <FormLabel component="legend">Kroon type</FormLabel>
                                    <RadioGroup
                                        row
                                        aria-label="kroon type"
                                        name="controlled-radio-buttons-group-crown"
                                        value={buildingProps.crownType}
                                        onChange={(event) => handleSelect(event, 'crownType')}
                                    >
                                        <FormControlLabel value={crownType.H} control={<Radio size="small" />} label="Maisonettes" />
                                        <FormControlLabel value={crownType.G} control={<Radio size="small" />} label="Appartementen" />
                                    </RadioGroup>
                                </FormControl>
                                {
                                    buildingProps.crownType === crownType.G ?
                                        <dl>
                                            <dt>
                                                Kroon verdiepingen</dt>
                                            <dd>
                                                {buildingProps.crownFloors}
                                            </dd>
                                        </dl>
                                        :
                                        <div className={buildingConfStyles.formControl}>
                                            <FormControl size="medium" fullWidth>
                                                <InputLabel id={"crown-floors-selection-label"}>Kroon verdiepingen</InputLabel>
                                                <Select
                                                    labelId={"crown-floors-selection-label"}
                                                    id={"crown-floors-selection-id"}
                                                    data-testid="crown-floors-selection-id"
                                                    value={buildingProps.crownFloors}
                                                    onChange={(e) => {
                                                        handleSelect(e as any, 'crownFloors')
                                                    }}
                                                    label="Kroon verdiepingen"
                                                >
                                                    <MenuItem data-testid={'crown-floors-selection-' + 0} value={2}>
                                                        2
                                                    </MenuItem>
                                                    <MenuItem data-testid={'crown-floors-selection-' + 0} value={3}>
                                                        3
                                                    </MenuItem>
                                                </Select>
                                            </FormControl>
                                        </div>
                                }
                            </div>
                        </div>)}
                    <hr></hr>
                    <div className="group">
                        <h2 className="greyed-out-text">Koppen</h2>
                        <div className={buildingConfStyles.formControl}>
                            <FormGroup>
                                {buildingProps.moosPlazaAlignment !== alignment.left &&
                                    <>
                                        <FormControlLabel control={<Switch
                                            checked={buildingProps.headLeftType !== headType.none}
                                            onChange={updateNoneHeadLeft}
                                        />} label="Kop links" />
                                        {
                                            buildingProps.headLeftType !== headType.none &&
                                            <FormControlLabel control={<Switch
                                                checked={HybridService.getHeadTypeAlignmentToBuilding(buildingProps.headLeftType) === alignsWithBuilding.perpendicular}
                                                onChange={updateHeadLeftAlignemntOptions}
                                            />} label="Haaks op gebouw" />
                                        }
                                    </>
                                }
                            </FormGroup>
                            {
                                buildingProps.headLeftType !== headType.none && getSelectFormHeadOptions('left')
                            }
                        </div>
                        <div className={buildingConfStyles.formControl}>
                            <FormGroup>
                                {buildingProps.moosPlazaAlignment !== alignment.right &&
                                    <>
                                        <FormControlLabel control={<Switch
                                            checked={buildingProps.headRightType !== headType.none}
                                            onChange={updateNoneHeadRight}
                                        />} label="Kop rechts" />
                                        {
                                            buildingProps.headRightType !== headType.none &&
                                            <FormControlLabel control={<Switch
                                                checked={HybridService.getHeadTypeAlignmentToBuilding(buildingProps.headRightType) === alignsWithBuilding.perpendicular}
                                                onChange={updateHeadRightAlignemntOptions}
                                            />} label="Haaks op gebouw" />
                                        }
                                    </>
                                }
                            </FormGroup>
                            {
                                buildingProps.headRightType !== headType.none && getSelectFormHeadOptions('right')
                            }
                        </div>
                    </div>
                </>}
        </div >
    )
};

export default Hybrid;
